import { Dispatch } from 'redux';
import { updateGlobalElapsedTime, updateRepElapsedTime, handleTimerStatus } from '../actions/activities.actions';

/**
 * Enterprise-Grade Timer Service
 * 
 * Single source of truth for all timing operations.
 * Implements proper error handling, state management, and edge case handling.
 * 
 * Features:
 * - Singleton pattern with proper initialization
 * - Comprehensive error handling
 * - State validation and recovery
 * - Event-driven architecture
 * - Memory leak prevention
 * - Thread-safe operations
 */
export class TimerService {
  private static instance: TimerService | null = null;
  private dispatch: Dispatch<any> | null = null;
  private isInitialized: boolean = false;
  
  // Timer state
  private globalStartTime: number | null = null;
  private repStartTime: number | null = null;
  private pausedDuration: number = 0;
  private pauseStartTime: number | null = null;
  
  // Timer intervals
  private globalInterval: NodeJS.Timeout | null = null;
  private repInterval: NodeJS.Timeout | null = null;
  
  // State flags
  private isGlobalRunning: boolean = false;
  private isRepRunning: boolean = false;
  private isPaused: boolean = false;
  
  // Event listeners
  private listeners: Map<string, Set<(data: any) => void>> = new Map();
  
  // Error handling
  private errorCount: number = 0;
  private maxErrors: number = 5;
  
  private constructor() {
    this.setupEventListeners();
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): TimerService {
    if (!TimerService.instance) {
      TimerService.instance = new TimerService();
    }
    return TimerService.instance;
  }

  /**
   * Initialize the timer service
   */
  public initialize(dispatch: Dispatch<any>): void {
    if (this.isInitialized) {
      console.warn('TimerService: Already initialized');
      return;
    }

    if (!dispatch) {
      throw new Error('TimerService: Dispatch function is required');
    }

    this.dispatch = dispatch;
    this.isInitialized = true;
    
    console.log('TimerService: Initialized successfully');
    this.emit('initialized', { timestamp: Date.now() });
  }

  /**
   * Start global timer
   */
  public startGlobalTimer(): void {
    if (!this.validateInitialization()) return;
    
    if (this.isGlobalRunning) {
      console.warn('TimerService: Global timer already running');
      return;
    }

    try {
      this.globalStartTime = Date.now();
      this.pausedDuration = 0;
      this.isGlobalRunning = true;
      this.isPaused = false;
      
      // Clear any existing interval
      this.clearGlobalInterval();
      
      // Start new interval
      this.globalInterval = setInterval(() => {
        this.updateGlobalTimer();
      }, 100);
      
      console.log('TimerService: Global timer started');
      this.emit('globalTimerStarted', { timestamp: this.globalStartTime });
      
    } catch (error) {
      this.handleError('startGlobalTimer', error);
    }
  }

  /**
   * Start rep timer
   * If already running, resets and restarts it
   */
  public startRepTimer(): void {
    if (!this.validateInitialization()) return;
    
    // If already running, reset it first
    if (this.isRepRunning) {
      console.log('TimerService: Rep timer already running, resetting first');
      this.resetRepTimer();
    }

    try {
      this.repStartTime = Date.now();
      this.isRepRunning = true;
      
      // Clear any existing interval (should be clear from reset, but just in case)
      this.clearRepInterval();
      
      // Start new interval
      this.repInterval = setInterval(() => {
        this.updateRepTimer();
      }, 100);
      
      console.log('TimerService: Rep timer started');
      this.emit('repTimerStarted', { timestamp: this.repStartTime });
      
    } catch (error) {
      this.handleError('startRepTimer', error);
    }
  }

  /**
   * Reset rep timer (stop and clear)
   */
  public resetRepTimer(): void {
    if (!this.validateInitialization()) return;
    
    try {
      this.clearRepInterval();
      this.repStartTime = null;
      this.isRepRunning = false;
      
      // Update Redux state
      this.dispatch!(updateRepElapsedTime(0));
      
      console.log('TimerService: Rep timer reset');
      this.emit('repTimerReset', { timestamp: Date.now() });
      
    } catch (error) {
      this.handleError('resetRepTimer', error);
    }
  }

  /**
   * Pause timers
   */
  public pauseTimers(): void {
    if (!this.validateInitialization()) return;
    
    if (this.isPaused) {
      console.warn('TimerService: Timers already paused');
      return;
    }

    try {
      this.isPaused = true;
      this.pauseStartTime = Date.now();
      
      // Clear intervals but keep state
      this.clearGlobalInterval();
      this.clearRepInterval();
      
      // Update Redux state
      this.dispatch!(handleTimerStatus('pause'));
      
      console.log('TimerService: Timers paused');
      this.emit('timersPaused', { timestamp: this.pauseStartTime });
      
    } catch (error) {
      this.handleError('pauseTimers', error);
    }
  }

  /**
   * Resume timers
   */
  public resumeTimers(): void {
    if (!this.validateInitialization()) return;
    
    if (!this.isPaused) {
      console.warn('TimerService: Timers not paused');
      return;
    }

    try {
      if (this.pauseStartTime) {
        this.pausedDuration += Date.now() - this.pauseStartTime;
        this.pauseStartTime = null;
      }
      
      this.isPaused = false;
      
      // Restart intervals if they were running
      if (this.isGlobalRunning) {
        this.startGlobalTimer();
      }
      if (this.isRepRunning) {
        this.startRepTimer();
      }
      
      console.log('TimerService: Timers resumed');
      this.emit('timersResumed', { timestamp: Date.now() });
      
    } catch (error) {
      this.handleError('resumeTimers', error);
    }
  }

  /**
   * Stop all timers
   */
  public stopAllTimers(): void {
    if (!this.validateInitialization()) return;
    
    try {
      this.clearGlobalInterval();
      this.clearRepInterval();
      
      this.isGlobalRunning = false;
      this.isRepRunning = false;
      this.isPaused = false;
      
      // Update Redux state
      this.dispatch!(handleTimerStatus('stop'));
      
      console.log('TimerService: All timers stopped');
      this.emit('allTimersStopped', { timestamp: Date.now() });
      
    } catch (error) {
      this.handleError('stopAllTimers', error);
    }
  }

  /**
   * Reset all timers
   */
  public resetAllTimers(): void {
    if (!this.validateInitialization()) return;
    
    try {
      this.stopAllTimers();
      
      this.globalStartTime = null;
      this.repStartTime = null;
      this.pausedDuration = 0;
      this.pauseStartTime = null;
      
      // Update Redux state
      this.dispatch!(updateGlobalElapsedTime(0));
      this.dispatch!(updateRepElapsedTime(0));
      
      console.log('TimerService: All timers reset');
      this.emit('allTimersReset', { timestamp: Date.now() });
      
    } catch (error) {
      this.handleError('resetAllTimers', error);
    }
  }

  /**
   * Get current global elapsed time
   */
  public getGlobalElapsedTime(): number {
    if (!this.globalStartTime) return 0;
    
    const now = Date.now();
    const baseElapsed = (now - this.globalStartTime) / 1000;
    return Math.max(0, baseElapsed - this.pausedDuration);
  }

  /**
   * Get current rep elapsed time
   */
  public getRepElapsedTime(): number {
    if (!this.repStartTime) return 0;
    
    const now = Date.now();
    return (now - this.repStartTime) / 1000;
  }

  /**
   * Get timer state
   */
  public getState(): {
    isInitialized: boolean;
    isGlobalRunning: boolean;
    isRepRunning: boolean;
    isPaused: boolean;
    globalElapsed: number;
    repElapsed: number;
  } {
    return {
      isInitialized: this.isInitialized,
      isGlobalRunning: this.isGlobalRunning,
      isRepRunning: this.isRepRunning,
      isPaused: this.isPaused,
      globalElapsed: this.getGlobalElapsedTime(),
      repElapsed: this.getRepElapsedTime()
    };
  }

  /**
   * Add event listener
   */
  public on(event: string, callback: (data: any) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  /**
   * Remove event listener
   */
  public off(event: string, callback: (data: any) => void): void {
    if (this.listeners.has(event)) {
      this.listeners.get(event)!.delete(callback);
    }
  }

  /**
   * Clean up resources
   */
  public destroy(): void {
    this.stopAllTimers();
    this.listeners.clear();
    this.dispatch = null;
    this.isInitialized = false;
    TimerService.instance = null;
    
    console.log('TimerService: Destroyed');
  }

  // Private methods

  private validateInitialization(): boolean {
    if (!this.isInitialized) {
      console.error('TimerService: Not initialized. Call initialize() first.');
      return false;
    }
    if (!this.dispatch) {
      console.error('TimerService: Dispatch not available');
      return false;
    }
    return true;
  }

  private updateGlobalTimer(): void {
    if (!this.dispatch || this.isPaused) return;
    
    try {
      const elapsed = this.getGlobalElapsedTime();
      // Write to globalTimer.elapsedTime — NOT the shared elapsedTime slice.
      // elapsedTime is owned by Timer.tsx (per-rep) so it can reset each rep.
      this.dispatch(updateGlobalElapsedTime(elapsed));
      this.emit('globalTimerUpdate', { elapsed });
    } catch (error) {
      this.handleError('updateGlobalTimer', error);
    }
  }

  private updateRepTimer(): void {
    if (!this.dispatch || this.isPaused) return;
    
    try {
      const elapsed = this.getRepElapsedTime();
      this.dispatch(updateRepElapsedTime(elapsed));
      this.emit('repTimerUpdate', { elapsed });
    } catch (error) {
      this.handleError('updateRepTimer', error);
    }
  }

  private clearGlobalInterval(): void {
    if (this.globalInterval) {
      clearInterval(this.globalInterval);
      this.globalInterval = null;
    }
  }

  private clearRepInterval(): void {
    if (this.repInterval) {
      clearInterval(this.repInterval);
      this.repInterval = null;
    }
  }

  private setupEventListeners(): void {
    // Setup any default event listeners here
  }

  private emit(event: string, data: any): void {
    if (this.listeners.has(event)) {
      this.listeners.get(event)!.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`TimerService: Error in event listener for ${event}:`, error);
        }
      });
    }
  }

  private handleError(method: string, error: any): void {
    this.errorCount++;
    console.error(`TimerService: Error in ${method}:`, error);
    
    if (this.errorCount >= this.maxErrors) {
      console.error('TimerService: Too many errors, stopping service');
      this.stopAllTimers();
    }
    
    this.emit('error', { method, error, errorCount: this.errorCount });
  }
}

// Export singleton instance
export const timerService = TimerService.getInstance();
